import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import api from "../../services/api";
import { IoRefresh } from "react-icons/io5";
import { FaSearch } from "react-icons/fa";
import Loading from "../../components/Loader/Loading";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import MarkdownIt from "markdown-it";
import Modal from "../../components/common/Modal";
import { convertMarkdownTablesToHtml } from "../../utils/common";

const GenerateBlog = () => {
  const location = useLocation();
  const { city, domain, industry } = location.state || {};
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingBlog, setIsLoadingBlog] = useState(false);
  const [isLoadingPublish, setIsLoadingPublish] = useState(false);
  const [keywords, setKeywords] = useState(null);
  const [blog, setBlog] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedKeywords, setSelectedKeywords] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("");
  const [editFields, setEditFields] = useState({});
  const [isResponseModalOpen, setIsResponseModalOpen] = useState(false);
  console.log("🚀 ~ GenerateBlog ~ isResponseModalOpen:", isResponseModalOpen);
  const [apiResponse, setApiResponse] = useState(null);
  console.log("🚀 ~ GenerateBlog ~ apiResponse:", apiResponse);
  const getDetails = async (keyword) => {
    setIsLoading(true);
    try {
      // generate_keywords
      const res = await api.post(`/api/domains/seo/generate_lsi_keywords`, {
        // domain: domain,
        primary_keyword: keyword,
      });
      if (res.status === 200) {
        setKeywords(res.data?.data);
      }
    } catch (error) {
      console.log(error);
      toast.error(error?.response?.data?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleView = async () => {
    setIsLoadingBlog(true);
    try {
      const res = await api.post(`/api/domains/seo/generate_blog`, {
        city: city,
        domain: domain,
        keyword: searchTerm,
        lsi_keywords: selectedKeywords?.map((item) => item.keyword).join(","),
        service: industry,
      });
      if (res.status === 200) {
        let blogData = res.data?.data;
        if (typeof blogData === "string") {
          try {
            blogData = JSON.parse(blogData);
          } catch (e) {
            console.error("Failed to parse blog data:", e);
          }
        }
        setBlog(blogData);

        // Initialize editFields and activeTab dynamically based on blog data
        if (blogData && typeof blogData === "object") {
          const dynamicEditFields = {};
          Object.keys(blogData).forEach((key) => {
            dynamicEditFields[key] = blogData[key] || "";
          });
          setEditFields(dynamicEditFields);

          // Set first key as active tab
          const firstKey = Object.keys(blogData)[0];
          if (firstKey) {
            setActiveTab(firstKey);
          }
        }
      }
    } catch (error) {
      console.log(error);
      toast.error(error?.response?.data?.message);
    } finally {
      setIsLoadingBlog(false);
    }
  };

  const handlePublish = async () => {
    setIsLoadingPublish(true);
    try {
      const res = await api.post(`/api/domains/seo/publish_blog`, {
        blog: blog,
        domain: domain,
        imagePrompt: `Generate a realistic image of a ${searchTerm} technician working in a home in ${city} USA. High realism, clean tools, local environment. Do not look like stock photo.`,
      });
      console.log("🚀 ~ handlePublish ~ res:", res);
      // Store the response data for the modal
      setApiResponse(res);
      // Show the response modal
      setIsResponseModalOpen(true);

      if (res.status === 200) {
        toast.success("Blog published successfully");
      } else {
        toast.error(res.data.message || "Failed to publish blog");
      }
    } catch (error) {
      console.log("🚀 ~ handlePublish ~ error:", error);
      toast.error(error.response?.data?.message || "Failed to publish blog");
      // Store the error response for the modal
      setApiResponse(error?.response);
      // Show the response modal
      setIsResponseModalOpen(true);
    } finally {
      setIsLoadingPublish(false);
    }
  };

  const handleRefresh = () => {
    if (searchTerm && selectedKeywords?.length) {
      handleView();
    }
  };

  const handleSearch = (keyword) => {
    if (domain && keyword) {
      getDetails(keyword);
      setBlog(null);
    }
  };

  const handleKeywordToggle = (keyword) => {
    setSelectedKeywords((prev) => {
      const isSelected = prev.some((item) => item.keyword === keyword.keyword);
      if (isSelected) {
        return prev.filter((item) => item.keyword !== keyword.keyword);
      } else {
        return [...prev, keyword];
      }
    });
  };

  const renderCards = () => {
    return keywords?.map((item, idx) => (
      <div className="flex gap-2" key={idx}>
        <div
          className={`border-2 rounded-lg p-4 mb-3 shadow-sm w-full cursor-pointer transition-all duration-200 ${
            selectedKeywords.some(
              (selected) => selected.keyword === item.keyword
            )
              ? "border-blue-500 bg-blue-100 shadow-lg"
              : "border-gray-200 bg-gray-50"
          }`}
          style={{
            borderColor: selectedKeywords.some(
              (selected) => selected.keyword === item.keyword
            )
              ? "#3b82f6"
              : "#e5e7eb",
            backgroundColor: selectedKeywords.some(
              (selected) => selected.keyword === item.keyword
            )
              ? "#dbeafe"
              : "#f9fafb",
          }}
          onClick={() => handleKeywordToggle(item)}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-500">Click to select</span>
            {selectedKeywords.some(
              (selected) => selected.keyword === item.keyword
            ) && (
              <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                <svg
                  className="w-3 h-3 text-[#3b82f6]"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            )}
          </div>
          <div>
            <span className="font-semibold">Keyword:</span> {item.keyword}
          </div>
          <div>
            <span className="font-semibold">Volume:</span> {item.volume}
          </div>
          <div>
            <span className="font-semibold">Difficulty:</span>{" "}
            {item.difficulty !== null ? item.difficulty : "N/A"}
          </div>
        </div>
      </div>
    ));
  };

  const formatMarkdown = (text) => {
    if (!text || typeof text !== "string") {
      return "";
    }
    let cleanedText = text;
    // Step 1: Fix double-escaped sequences
    cleanedText = cleanedText
      .replace(/\\\\n/g, "\n")
      .replace(/\\\\/g, "\\")
      .replace(/\\n/g, "\n")
      .replace(/\\r/g, "\r")
      .replace(/\\t/g, "\t");
    // Step 2: Normalize line endings to \n
    cleanedText = cleanedText.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
    return cleanedText;
  };

  // const parseMarkdownToServiceFormat = (markdownContent) => {
  //   if (!markdownContent || typeof markdownContent !== "string") {
  //     return {};
  //   }

  //   // Split content by headings (##)
  //   const sections = markdownContent.split(/\n## /);

  //   if (sections.length < 2) {
  //     return {};
  //   }

  //   // Remove the first element if it's just the main title
  //   const mainTitle = sections[0].replace(/^# /, "").trim();
  //   const headingSections = sections.slice(1);

  //   const result = {
  //     slogan_1: mainTitle,
  //   };

  //   // Process up to 4 heading sections
  //   headingSections.slice(0, 4).forEach((section, index) => {
  //     const lines = section.split("\n");
  //     const title = lines[0].trim();
  //     const content = lines.slice(1).join("\n").trim();

  //     const serviceNumber = index + 1;
  //     result[`title_${serviceNumber}`] = title;
  //     result[`text_${serviceNumber}`] = content;
  //   });

  //   return result;
  // };

  const markdownIt = new MarkdownIt({
    html: true,
    linkify: true,
    breaks: true,
  });

  return (
    <>
      {isLoading && <Loading />}
      <div className="flex h-[calc(100vh-50px)] gap-1">
        <div className="w-[30%] max-w-[30%] border border-gray-300 p-4 bg-white h-full overflow-y-auto">
          <div className="text-sm font-bold mb-4 flex items-center justify-center flex-col">
            <h3 className="text-lg font-bold mb-1">
              Enter Keywords to generate Blog
            </h3>
            <div className="text-sm text-gray-500">{`(${domain})`}</div>
          </div>
          <div className="relative mb-4">
            <input
              type="text"
              placeholder="Search keywords..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pr-12 pl-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            />
            <div className="absolute inset-y-0 right-0 pr-1 flex items-center">
              <button
                onClick={() => handleSearch(searchTerm)}
                className="!bg-[#042A42] text-white p-2 rounded-md hover:!bg-[#031a2e] transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#042A42] focus:ring-opacity-50 shadow-sm hover:shadow-md transform hover:scale-105"
                type="button"
              >
                <FaSearch className="h-4 w-4" />
              </button>
            </div>
          </div>

          {isLoading ? null : keywords && keywords.length > 0 ? (
            <>
              <div className="mb-4 flex justify-center">
                <button
                  onClick={handleView}
                  disabled={!selectedKeywords?.length}
                  className={`px-6 py-2 rounded-lg font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 ${
                    selectedKeywords?.length
                      ? "bg-[#2563eb] text-white hover:bg-blue-700 shadow-md hover:shadow-lg transform hover:scale-105"
                      : "bg-gray-300 text-gray-500 cursor-not-allowed"
                  }`}
                >
                  Generate Blog for Selected Keyword
                </button>
              </div>
              {renderCards()}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center text-gray-400">
              <span className="text-5xl mb-2">🔍</span>
              <div className="text-lg font-semibold mb-1">
                Search for a keyword
              </div>
              <div className="text-sm">
                {keywords
                  ? "No keywords match your search criteria."
                  : "Try selecting a different domain or check back later."}
              </div>
            </div>
          )}
        </div>
        <div className="w-[70%] max-w-[70%] border border-gray-300 p-4 bg-white h-full overflow-y-auto">
          {isLoadingBlog ? (
            <div className="flex items-center justify-center h-full">
              <img src="/img/loading.gif" className="w-16" alt="Loading..." />
            </div>
          ) : blog ? (
            <div className="mt-8 p-4 border border-blue-200 rounded-lg bg-blue-50">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-semibold mb-2">Blog Preview</h3>
                <div className="flex items-center gap-2">
                  <button
                    className="btnPrimary !bg-[#042A42] text-white font-bold py-2 px-5 rounded-lg shadow transition-colors duration-200 cursor-pointer"
                    onClick={handleRefresh}
                    disabled={isLoadingPublish}
                  >
                    {isLoadingPublish ? (
                      <img
                        src="/img/loading.gif"
                        className="w-5 h-5 mr-2"
                        alt="Publishing..."
                      />
                    ) : null}
                    <IoRefresh className=" w-5 h-5 " />
                  </button>
                  <button
                    className="btnPrimary !bg-[#042A42] text-white font-bold py-2 px-5 rounded-lg shadow transition-colors duration-200 cursor-pointer"
                    onClick={() => {
                      setIsOpen(true);
                    }}
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                  </button>
                </div>
              </div>
              {blog && (
                <div className="mb-2">
                  <span className="font-bold">Meta Title: </span>
                  <span className="tracking-wide">{blog.meta_title}</span>
                </div>
              )}
              {blog && (
                <div className="mb-2">
                  <span className="font-bold">Meta Heading: </span>
                  <span className="tracking-wide">{blog.meta_heading}</span>
                </div>
              )}
              {blog && (
                <div className="mb-2">
                  <span className="font-bold">Meta Description: </span>
                  <span className="tracking-wide">{blog.meta_description}</span>
                </div>
              )}
              {blog && (
                <div className="mb-2">
                  <span className="font-bold">Title: </span>
                  <span className="tracking-wide">{blog.title}</span>
                </div>
              )}
              {blog && (
                <div className="mb-2">
                  <span className="font-bold">Description: </span>
                  <div className="tracking-wide prose prose-sm max-w-none content-markdown">
                    <div
                      dangerouslySetInnerHTML={{
                        __html: markdownIt.render(
                          convertMarkdownTablesToHtml(
                            `${formatMarkdown(blog?.description)}`
                          )
                        ),
                      }}
                    />
                    {/* <div dangerouslySetInnerHTML={{ __html: htmlContent }} /> */}
                  </div>
                </div>
              )}
              <div className="flex justify-end mt-4">
                <button
                  className="btnPrimary !bg-[#042A42] text-white font-bold py-2 px-5 rounded-lg shadow transition-colors duration-200"
                  onClick={handlePublish}
                  disabled={isLoadingPublish}
                >
                  {isLoadingPublish ? (
                    <img
                      src="/img/loading.gif"
                      className="w-5 h-5 mr-2"
                      alt="Publishing..."
                    />
                  ) : null}
                  Publish Blog
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center  rounded-xl p-10 border-2 border-blue-200 shadow-md">
              <div className="text-3xl  mb-3 text-gray-500">
                No Blog Selected
              </div>
              <div className="text-lg text-gray-500">
                Select a keyword from the left to generate and preview a blog
                post here. The blog details will appear in this section once you
                select a keyword.
              </div>
            </div>
          )}
        </div>
      </div>
      <ToastContainer />
      <Modal
        open={!isOpen}
        handleModal={() => setIsOpen(false)}
        className="max-w-[50%]"
        title="Generate Blog"
        modalType="edit"
        handleModalSubmit={(e) => {
          e.preventDefault();
          setBlog((prev) => ({
            ...prev,
            ...editFields,
          }));
          setIsOpen(false);
        }}
      >
        <div>
          <div className="mb-3">
            <div className="flex gap-2 border-b">
              {blog &&
                Object.keys(blog)?.map((key, idx) => (
                  <button
                    key={idx}
                    type="button"
                    className={`px-3 py-2 text-sm border-b-2 -mb-px ${
                      activeTab === key
                        ? "border-[#0000FF] !text-blue-700 font-semibold"
                        : "border-transparent text-gray-600 hover:text-gray-800"
                    }`}
                    onClick={() => setActiveTab(key)}
                  >
                    {key
                      .replace(/_/g, " ")
                      .replace(/\b\w/g, (l) => l.toUpperCase())}
                  </button>
                ))}
            </div>
          </div>
          <div className="mb-4 h-full">
            <textarea
              value={editFields[activeTab] || ""}
              onChange={(e) => {
                const value = e.target.value;
                setEditFields((prev) => ({ ...prev, [activeTab]: value }));
              }}
              className="w-full h-[450px] border border-[#0000FF] rounded-md p-3 font-mono text-sm resize-none"
              placeholder={`Enter ${activeTab.replace(/_/g, " ")} here...`}
            />
          </div>
        </div>
      </Modal>

      {/* API Response Modal */}
      {isResponseModalOpen ? (
        <Modal
          open={false}
          handleModal={() => {
            setIsResponseModalOpen(false);
            setApiResponse(null);
          }}
          className="max-w-[60%]"
          title={`API Response - ${
            apiResponse?.type === "success" ? "Success" : "Error"
          }`}
          modalType="view"
          closeOnly
        >
          <div className="space-y-4">
            {/* Status Badge */}
            <div className="flex items-center gap-3">
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  apiResponse?.type === "success"
                    ? "bg-green-100 text-green-800"
                    : "bg-red-100 text-red-800"
                }`}
              >
                {apiResponse?.type === "success" ? "✓ Success" : "✗ Error"}
              </span>
              <span className="text-sm text-gray-600">
                Status Code: {apiResponse?.status}
              </span>
            </div>

            {/* Message */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-semibold text-gray-800 mb-2">Message:</h4>
              <p
                className={`text-sm ${
                  apiResponse?.type === "success"
                    ? "text-green-700"
                    : "text-red-700"
                }`}
              >
                {apiResponse?.message || apiResponse?.data?.message}
              </p>
            </div>

            {/* Full Response Data */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-semibold text-gray-800 mb-2">
                Full Response:
              </h4>
              <div className="bg-white border rounded-lg p-3 max-h-64 overflow-y-auto">
                <pre className="text-xs text-gray-700 whitespace-pre-wrap">
                  {JSON.stringify(apiResponse?.data, null, 2)}
                </pre>
              </div>
            </div>

            {/* Close Button */}
            {/* <div className="flex justify-end pt-4 border-t">
              <button
                onClick={() => {
                  setIsResponseModalOpen(false);
                  setApiResponse(null);
                }}
                className={`px-6 py-2 rounded-lg font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-opacity-50 ${
                  apiResponse?.type === "success"
                    ? "bg-green-600 text-white hover:bg-green-700 focus:ring-green-500"
                    : "bg-red-600 text-white hover:bg-red-700 focus:ring-red-500"
                }`}
              >
                Close
              </button>
            </div> */}
          </div>
        </Modal>
      ) : null}
    </>
  );
};

export default GenerateBlog;
